/**
 * „Was ist zu tun" — die erste Karte auf dem Dashboard.
 *
 * Der Ablauf, für den sie gebaut ist: Der Nutzer handelt im Paper-Konto
 * dieser App und bildet dieselben Trades bei Revolut nach. Er braucht genau
 * einen Satz mit den konkreten Preisen — und zwar zuverlässig, nicht als
 * Mail, die man überscrollt.
 *
 * Deshalb wird die Liste bei jedem Aufruf aus den Kursen neu abgeleitet
 * statt aus verschickten Nachrichten. Eine fällige Handlung bleibt stehen,
 * bis sie erledigt ist — auch nach drei Tagen ohne Blick in die App.
 *
 * „Heute nichts zu tun" ist ein vollwertiges Ergebnis und wird als solches
 * angezeigt. Bei rund zehn Signalen im Jahr ist es der Normalfall, und eine
 * App, die daraus ein leeres Feld macht, erzieht zum Suchen nach Gründen.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowDownToLine, CheckCircle2, FlaskConical, ShoppingCart, MoveUp } from 'lucide-react';

import { useDemoMode } from '@/contexts/DemoModeContext';
import { leiteHandlungenAb, type Handlung } from '@/lib/v6/handlungen';
import { gehoertInHandlungsliste } from '@/lib/v6/spuren';
import { UebernahmeDialog, type UebernahmeSignal } from './UebernahmeDialog';
import { fetchOffeneZonenSignale } from '@/lib/v6/zone-signals-read';
import { fetchPositions } from '@/lib/queries';
import { getMarketDataProvider } from '@/lib/market-data';
import type { OhlcvBar } from '@/lib/market-data/types';
import { cn } from '@/lib/utils';

const BARS = 420;

const SYMBOLIK: Record<Handlung['art'], { icon: typeof AlertTriangle; farbe: string }> = {
  ausgestoppt:     { icon: ArrowDownToLine, farbe: 'text-rose-300' },
  teilverkauf:     { icon: ArrowDownToLine, farbe: 'text-amber-300' },
  kaufen:          { icon: ShoppingCart,    farbe: 'text-emerald-300' },
  stop_nachziehen: { icon: MoveUp,          farbe: 'text-sky-300' },
};

export function HandlungslisteCard() {
  const [handlungen, setHandlungen] = useState<Handlung[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  // Welches Kaufsignal gerade übernommen wird — null = kein Dialog offen.
  /** Signal PLUS Spur — derselbe Dialog bucht echt oder auf Papier. */
  const [uebernahme, setUebernahme] = useState<
    { signal: UebernahmeSignal; spur: 'echt' | 'papier' } | null
  >(null);
  const [gebucht, setGebucht] = useState<string[]>([]);
  // Diese Karte laeuft auch unter /demo — DemoApp rendert das echte Dashboard.
  // Ein direkter Pfad wuerde einen Demo-Besucher in die geschuetzte App werfen
  // und ihn dort auf der Loginseite abladen. linkTo setzt das /demo-Praefix.
  const { linkTo } = useDemoMode();

  useEffect(() => {
    let ab = false;
    // Ein eigener Controller, damit ein Seitenwechsel die Abfragen abbricht,
    // statt in setState auf einer nicht mehr sichtbaren Karte zu landen.
    const controller = new AbortController();
    (async () => {
      try {
        const [positionen, zonen] = await Promise.all([
          fetchPositions('open', controller.signal),
          fetchOffeneZonenSignale(controller.signal),
        ]);
        const symbole = [
          ...new Set([
            ...positionen.map((p) => p.symbol.toUpperCase()),
            ...zonen.map((z) => z.symbol.toUpperCase()),
          ]),
        ];
        const provider = getMarketDataProvider();
        const bars = new Map<string, OhlcvBar[]>();
        await Promise.all(
          symbole.map(async (s) => {
            const r = await provider.getOhlcv(s, 'daily', BARS);
            bars.set(s, r.data ?? []);
          })
        );
        if (ab) return;
        setHandlungen(
          leiteHandlungenAb(
            positionen
              // 4b (11.08.2026): Experimente aus dem Papierkonto bleiben hier
              // draussen. „Was ist zu tun" ist die Liste fuer echtes Geld —
              // ein Versuch darf nicht mit einem echten Auftrag um die
              // Aufmerksamkeit konkurrieren. Verwaltet werden sie trotzdem,
              // taeglich im Cron (paper-verwaltung.ts).
              .filter((p) => gehoertInHandlungsliste({
                broker_account: p.broker_account,
                created_by: p.created_by,
              }))
              .map((p) => ({
              symbol: p.symbol.toUpperCase(),
              einstieg: Number(p.avg_entry_price),
              stop: Number(p.current_stop),
              initialStop: Number(p.initial_stop ?? p.current_stop),
              einstiegAm: String(p.entry_at ?? '').slice(0, 10),
            })),
            zonen.map((z) => ({
              symbol: z.symbol.toUpperCase(),
              ausloeser: z.ausloeser,
              limit: z.entry_price,
              stop: z.stop_price,
              seit: z.generated_date,
              zoneSignalId: z.id,
            })),
            bars
          )
        );
      } catch (e) {
        if (!ab) setFehler(String(e));
      }
    })();
    return () => { ab = true; controller.abort(); };
  }, []);

  if (fehler) {
    return (
      <div className="cockpit-card p-4 text-[13px] text-zinc-400">
        Handlungsliste nicht berechenbar: {fehler}
      </div>
    );
  }
  if (handlungen === null) {
    return <div className="cockpit-card p-4"><div className="skeleton h-16 w-full" /></div>;
  }

  const dringend = handlungen.filter((h) => h.dringend).length;

  return (
    <div
      className={cn(
        'cockpit-card p-4 space-y-3',
        dringend > 0 && 'ring-1 ring-amber-500/40'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-sm font-semibold">Was ist zu tun</h2>
        <span className="text-[10px] uppercase tracking-wide text-zinc-500">
          aus den Kursen abgeleitet
        </span>
      </div>

      {handlungen.length === 0 ? (
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400/80" />
          <div>
            <p className="text-[13px] text-zinc-200">Heute nichts zu tun.</p>
            <p className="text-[11px] text-zinc-500 leading-snug">
              Kein Stop gerissen, kein Teilverkauf fällig, keine Kaufzone offen.
              Bei rund zehn Signalen im Jahr ist das der Normalzustand.
            </p>
          </div>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {handlungen.map((h, i) => {
            const s = SYMBOLIK[h.art];
            const Icon = s.icon;
            return (
              <li
                key={`${h.symbol}-${h.art}-${i}`}
                className={cn(
                  'rounded border px-3 py-2.5 space-y-1',
                  h.dringend
                    ? 'border-amber-500/30 bg-amber-500/[0.06]'
                    : 'border-zinc-800/60'
                )}
              >
                <div className="flex items-center gap-2">
                  <Icon className={cn('w-4 h-4 shrink-0', s.farbe)} />
                  {/* Der ganze Satz ist der Link, nicht nur das Kürzel: Wer
                      "PDD kaufen" liest, will den Wert ansehen — und am Handy
                      ist ein Satz ein Ziel, das man trifft, ein Kürzel nicht.
                      Layer ist fest "swing": leiteHandlungenAb() filtert Krypto
                      (Symbole mit "/") heraus, hier kommen nur Aktien an. */}
                  <Link
                    to={linkTo(`/symbol/swing/${encodeURIComponent(h.symbol)}`)}
                    className="text-[13px] font-medium hover:underline underline-offset-2
                               decoration-zinc-500 focus:outline-none focus-visible:underline"
                  >
                    {h.satz}
                  </Link>
                  {h.tageOffen > 0 && (
                    <span className="ml-auto text-[10px] text-amber-300/90 whitespace-nowrap">
                      seit {h.tageOffen} Handelstag{h.tageOffen === 1 ? '' : 'en'} fällig
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-zinc-400 leading-snug pl-6">{h.grund}</p>
                {/* Der Übergang von der Beobachtung zur Ausführung. Nur bei
                    Kaufzonen — und nur, solange dieses Signal in dieser
                    Sitzung nicht schon übernommen wurde. */}
                {/* Kein Kauf-Knopf, wenn der Kurs über das Limit gelaufen ist —
                    die Order wäre nicht ausführbar (handlungen.ts, `kaufbar`). */}
                {h.art === 'kaufen' && h.limit !== null && h.stop !== null && (
                  <div className="pl-6 pt-0.5 space-y-1">
                    {gebucht.includes(h.symbol) ? (
                      <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300/90">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Gebucht. Erscheint beim nächsten Laden unter „Was läuft".
                      </span>
                    ) : (
                      <>
                        {/* Befund 6 (12.08.2026): Der Knopf war bei
                            `kaufbar === false` GANZ verschwunden. Wer trotzdem
                            gekauft hatte — bewusst, oder früher am Tag zum
                            besseren Kurs —, konnte es nirgends mehr eintragen.
                            Jetzt zurückgestuft statt versteckt: Die Warnung
                            hält vom Hinterherlaufen ab, ohne eine Sackgasse zu
                            bauen. */}
                        {h.kaufbar === false && (
                          <p className="text-[11px] text-amber-300/80 leading-snug">
                            Der Kurs steht über dem Limit — nur eintragen, wenn du
                            wirklich gekauft hast.
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() =>
                              setUebernahme({
                                signal: {
                                  symbol: h.symbol,
                                  limit: h.limit!,
                                  stop: h.stop!,
                                  ausloeser: h.ausloeser ?? null,
                                  zoneSignalId: h.zoneSignalId ?? null,
                                },
                                spur: 'echt',
                              })
                            }
                            className={cn(
                              'inline-flex items-center gap-1.5 rounded border px-2 py-1 text-[11px]',
                              h.kaufbar === false
                                ? 'border-zinc-700 bg-zinc-800/40 text-zinc-300 hover:bg-zinc-800'
                                : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20'
                            )}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Habe ich gekauft
                          </button>

                          {/* Befund 3: Ein Systemsignal auf Papier hatte bisher
                              nur den Experiment-Weg — und der bedeutet „ohne
                              Systemvorschlag", also ein falsches Etikett.
                              Bewusst unauffälliger als der echte Kauf. */}
                          <button
                            onClick={() =>
                              setUebernahme({
                                signal: {
                                  symbol: h.symbol,
                                  limit: h.limit!,
                                  stop: h.stop!,
                                  ausloeser: h.ausloeser ?? null,
                                  zoneSignalId: h.zoneSignalId ?? null,
                                },
                                spur: 'papier',
                              })
                            }
                            className="inline-flex items-center gap-1.5 rounded border
                                       border-sky-500/25 bg-sky-500/5 px-2 py-1
                                       text-[11px] text-sky-200/80 hover:bg-sky-500/15"
                          >
                            <FlaskConical className="w-3.5 h-3.5" />
                            Auf Papier mitnehmen
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {(h.limit !== null || h.stop !== null) && (
                  <div className="pl-6 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] tabular-nums">
                    {h.limit !== null && (
                      <span className="text-zinc-300">
                        Limit <span className="font-medium">{h.limit.toFixed(2)}</span>
                      </span>
                    )}
                    {h.stop !== null && (
                      <span className="text-zinc-300">
                        Stop <span className="font-medium">{h.stop.toFixed(2)}</span>
                      </span>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {uebernahme && (
        <UebernahmeDialog
          signal={uebernahme.signal}
          spur={uebernahme.spur}
          onAbbruch={() => setUebernahme(null)}
          onGebucht={() => {
            setGebucht((g) => [...g, uebernahme.signal.symbol]);
            setUebernahme(null);
          }}
        />
      )}

      {dringend > 0 && (
        <p className="text-[10px] text-zinc-500 leading-snug border-t border-zinc-800/60 pt-2 flex gap-1.5">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0 text-amber-400/70" />
          <span>
            Hervorgehobene Punkte kosten Geld, wenn sie liegen bleiben. Sie
            verschwinden erst, wenn die Position entsprechend gebucht ist —
            nicht durch Wegklicken.
          </span>
        </p>
      )}
    </div>
  );
}
